"use client";

import { PrivyProvider } from "@privy-io/react-auth";
import { WagmiProvider } from "@privy-io/wagmi";
import { base, baseSepolia } from "viem/chains";
import { wagmiConfig } from "@/lib/wagmi-config";

const chainId = process.env.NEXT_PUBLIC_CHAIN_ID;
const chain = chainId === "8453" ? base : baseSepolia;

export function PrivyClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID;

  if (!appId) {
    console.warn("NEXT_PUBLIC_PRIVY_APP_ID not set - auth disabled");
    return <>{children}</>;
  }

  return (
    <PrivyProvider
      appId={appId}
      config={{
        appearance: {
          theme: "dark",
          accentColor: "#FF3D3D",
          showWalletLoginFirst: true,
          // Lock the modal to EVM wallets only. Without this Privy will
          // also enumerate any Solana wallets that announce themselves
          // via EIP-6963/window.ethereum (notably Phantom), which causes
          // the modal to surface BOTH MetaMask and Phantom's injected
          // EIP-1193 provider for the same connector and ends with the
          // dreaded "Request of type 'wallet_requestPermissions' already
          // pending" error in MetaMask. We're an EVM-only app, so this
          // is the correct constraint.
          walletChainType: "ethereum-only",
          walletList: [
            "metamask",
            "coinbase_wallet",
            "rainbow",
            "wallet_connect",
            "detected_ethereum_wallets",
          ],
        },

        loginMethods: ["wallet", "twitter"],

        embeddedWallets: {
          ethereum: {
            createOnLogin: "users-without-wallets",
          },
          showWalletUIs: false,
          // Signing for DB/API actions (plinth, chat verify) should use
          // explicit `signMessage` / server routes — avoid prompting SIWE on
          // every generic connect when possible. Tune remaining behaviour in
          // the Privy dashboard (login methods / SIWE) if a prompt persists.
        },

        defaultChain: chain,
        supportedChains: [chain],

        fundingMethodConfig: {
          moonpay: {
            useSandbox: chain.id === baseSepolia.id,
          },
        },
      }}
    >
      <WagmiProvider config={wagmiConfig}>
        {children}
      </WagmiProvider>
    </PrivyProvider>
  );
}
